import { ContainerWrapper } from '../Container'
import WhiteButton from '../Buttons/WhiteButton'
import { useEffect, useRef, useState } from 'react'
import SectionHeadingWrapper from './SectionHeadingWrapper'
import SectionHeading from './SectionHeading'
import QuoteIcon from '../../Icons/realEsatePostcards/quote.svg'
import { useRouter } from 'next/router'

const Counter = ({
    to = 0,
    from = 0,
    duration = 2000,
    suffix = '',
    className = '',
}) => {
    const [value, setValue] = useState(from)
    const ref = useRef(null)
    const startedRef = useRef(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !startedRef.current) {
                        startedRef.current = true
                        const startTime = performance.now()
                        const animate = (now) => {
                            const elapsed = now - startTime
                            const progress = Math.min(elapsed / duration, 1)
                            const current = Math.floor(
                                from + (to - from) * progress,
                            )
                            setValue(current)
                            if (progress < 1) requestAnimationFrame(animate)
                        }
                        requestAnimationFrame(animate)
                    }
                })
            },
            { threshold: 0.2 },
        )

        observer.observe(el)
        return () => observer.disconnect()
    }, [to, from, duration])

    return (
        <span
            ref={ref}
            className={`elementor-counter-number ${className}`}
            data-duration={duration}
            data-to-value={to}
            data-from-value={from}>
            {value}
            {suffix}
        </span>
    )
}

const defaultTestimonials = [
    'Fast, responsive, friendly, and effective support! Every campaign has been smooth and successful.',
    "I've uploaded my lists, used OLM postcards, and I'm already seeing responses. The designs are powerful for investors.",
]

const defaultStats = [
    {
        label: 'Trusted by Real <br> Estate Professionals',
        to: 1000,
        suffix: '+',
    },
    { label: 'ROI Generated for <br> Clients', to: 300, suffix: '%' },
    { label: 'Recognized by <br> Inc.', to: 5000, suffix: '' },
]

export default function InvestorsSay({
    heading = 'What Investors Say',
    ctaLabel = 'Get Free Samples',
    ctaHref = '/get-free-samples',
    promoText = "Trusted by 1,000+ real estate professionals. Recognized by Inc. 5000 as one of America's fastest-growing private companies.",
    testimonials = defaultTestimonials,
    stats = defaultStats,
}) {
    const router = useRouter()
    const Cta = (
        <WhiteButton
            onClick={() => {
                router.push(ctaHref)
            }}
            className="w-full sm:w-auto">
            <span className="olm-gradient-text">{ctaLabel}</span>
        </WhiteButton>
    )

    return (
        <section aria-labelledby="investors-say-heading" className="py-16">
            <ContainerWrapper>
                <div className="px-4">
                    <SectionHeadingWrapper>
                        <SectionHeading>{heading}</SectionHeading>
                    </SectionHeadingWrapper>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-5 max-w-[1200px] mx-auto">
                        {/* Left gradient promo card (spans two rows on desktop) */}
                        <div className="olm-gradient text-white rounded-3xl p-8 md:px-10 md:p-14 col-span-12 row-span-1 lg:col-span-4 lg:row-span-2 flex flex-col justify-between min-h-[360px] shadow-[0_12px_24px_rgba(255,101,47,0.18)]">
                            <p className="text-lg font-normal leading-relaxed md:text-xl">
                                {promoText}
                            </p>
                            <div className="mt-8">{Cta}</div>
                        </div>

                        {/* Top right: Testimonials */}
                        <div className="col-span-12 row-span-1 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                            {testimonials.map((t, idx) => (
                                <figure
                                    key={idx}
                                    className="bg-white rounded-2xl border border-orange-200 px-5 pt-1 pb-7 md:px-5 text-center shadow-[0_10px_24px_rgba(255,101,47,0.12)]">
                                    <div className="flex justify-center mb-3 md:mb-4">
                                        <QuoteIcon className="w-full h-16" />
                                    </div>
                                    <blockquote className="text-olm-new-black text-lg">
                                        {t}
                                    </blockquote>
                                </figure>
                            ))}
                        </div>

                        {/* Bottom right: Stats */}
                        <div className="col-span-12 row-span-1 lg:col-span-8 flex flex-wrap justify-center md:justify-between gap-2 md:gap-0 shadow-[0_10px_24px_rgba(255,101,47,0.12)] rounded-2xl border border-orange-200 p-5">
                            {stats.map((s, idx) => (
                                <div key={idx}>
                                    <div
                                        className="text-black mb-3 md:mb-4 text-lg font-medium"
                                        dangerouslySetInnerHTML={{
                                            __html: s.label,
                                        }}
                                    />
                                    <div className="text-orange-600 text-[40px] md:text-7xl font-semibold leading-none">
                                        <Counter
                                            to={s.to}
                                            from={0}
                                            duration={2000}
                                            suffix={s.suffix}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </ContainerWrapper>
        </section>
    )
}
